import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosEmergency } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";

/* ---------- HELPERS ---------- */
const asErr = (err) => {
  const data = err?.response?.data || err?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  return err?.message || "EmergencyContact thunk failed";
};

const unwrap = (x) => {
  if (!x) return {};
  if (x?.data !== undefined) return x.data;
  return x;
};

const unwrapContact = (raw) => {
  const x = unwrap(raw);
  if (x && typeof x === "object" && (x._id || x.id)) return x;
  if (x?.item && (x.item._id || x.item.id)) return x.item;
  if (x?.contact && (x.contact._id || x.contact.id)) return x.contact;
  if (raw?.data && (raw.data._id || raw.data.id)) return raw.data;
  return x;
};

/* ---------- LIST ---------- */
export const fetchEmergencyContacts = createAsyncThunk(
  "emergencyContacts/fetchAll",
  async ({ userId, subprofileId = null }, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId requis");

      const { data } = await axiosEmergency.get(
        ENDPOINTS.EMERGENCY.LIST(userId),
        {
          withCredentials: true,
          headers: { 
            "x-user-id": userId,
            ...(subprofileId ? { "x-subprofile-id": String(subprofileId) } : {}),
          },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        }
      );

      const x = unwrap(data);
      const items = Array.isArray(x)
        ? x
        : Array.isArray(x?.data)
        ? x.data
        : Array.isArray(x?.items)
        ? x.items
        : [];

      return { userId, items };
    } catch (err) {
      return rejectWithValue(asErr(err));
    }
  }
);

/* ---------- ADD ---------- */
export const addEmergencyContact = createAsyncThunk(
  "emergencyContacts/add",
  async ({ userId, contact }, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId requis");

      const payload = {
        name: String(contact?.name || "").trim(),
        phone: String(contact?.phone || "").trim(),
        relation: String(contact?.relation || "").trim() || undefined,
      };

      if (!payload.name || !payload.phone) {
        throw new Error("Nom et téléphone requis");
      }

      const { data } = await axiosEmergency.post(
        ENDPOINTS.EMERGENCY.CREATE(userId),
        payload,
        { withCredentials: true }
      );

      return unwrapContact(data);
    } catch (err) {
      return rejectWithValue(asErr(err));
    }
  }
);

/* ---------- GET ONE ---------- */
export const getEmergencyContactById = createAsyncThunk(
  "emergencyContacts/getOne",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      if (!userId || !id) throw new Error("userId et id requis");

      const { data } = await axiosEmergency.get(
        ENDPOINTS.EMERGENCY.BY_ID(userId, id),
        {
          withCredentials: true,
          headers: { "Cache-Control": "no-cache" },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        }
      );

      return unwrapContact(data);
    } catch (err) {
      return rejectWithValue(asErr(err));
    }
  }
);

/* ---------- UPDATE ---------- */
export const updateEmergencyContact = createAsyncThunk(
  "emergencyContacts/update",
  async ({ userId, id, patch }, { rejectWithValue }) => {
    try {
      if (!userId || !id) throw new Error("userId et id requis");

      const payload = {
        name: patch?.name ? String(patch.name).trim() : undefined,
        phone: patch?.phone ? String(patch.phone).trim() : undefined,
        relation: patch?.relation ? String(patch.relation).trim() : undefined,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      const url =
        ENDPOINTS.EMERGENCY.UPDATE(userId, id) || `/emergency-contact/update/${userId}/${id}`;

      const { data } = await axiosEmergency.put(url, payload, { withCredentials: true });

      return unwrapContact(data);
    } catch (err) {
      return rejectWithValue(asErr(err));
    }
  }
);

/* ---------- DELETE ---------- */
export const deleteEmergencyContact = createAsyncThunk(
  "emergencyContacts/delete",
  async ({ userId, id }, { rejectWithValue }) => {
    try {
      if (!userId || !id) throw new Error("userId et id requis");

      const res = await axiosEmergency.delete(
        ENDPOINTS.EMERGENCY.DELETE(userId, id),
        { withCredentials: true }
      );

      const returnedId =
        res?.data?.id ||
        res?.data?._id ||
        res?.data?.data?.id ||
        res?.data?.data?._id ||
        id;

      return { id: String(returnedId) };
    } catch (err) {
      return rejectWithValue(asErr(err));
    }
  }
);
